/**
 * Die Bewegung, mit der eine gezogene Aufgabe **landet**: Sie gleitet vom Overlay an ihren
 * Platz, poppt dabei kurz hoch (etwas größer, ein Pixel angehoben), federt leicht unter ihre
 * Größe und sinkt ab — das Gegenstück zum Anheben (`.drag-overlay-lift`, 1,02 mit Schatten).
 *
 * Die Werte stammen aus dem Prototyp `tools/drop-bounce-prototype` (Sven, 03.10.2026, #238).
 * Das Gleiten ist der frühere FLIP aus `useTaskDragAndDrop` (dieselbe Kurve, jetzt 350 statt
 * 200 ms); der Pop liegt obendrauf und beginnt schon auf dem Weg.
 *
 * **Stützstellen statt zweier Keyframes:** Zwei Bewegungen mit eigenen Zeiten und Kurven
 * überlagern sich; WAAPI kann das in einer Animation nur, wenn der Verlauf vorab gerechnet ist.
 * Zwischen den Stützstellen (~alle 8 ms) läuft es linear — so ist es exakt die Kurve aus dem
 * Prototyp.
 *
 * Bewusst ohne DOM-Bezug: Hier wird gerechnet, am Element eingesetzt, und geprüft ohne
 * gerendertes Bauteil.
 */

import { cubicBezier } from 'framer-motion'

export interface DropPopMotionOptions {
  /** Strecke vom Overlay zum Zielplatz (Overlay minus Ziel), in Pixeln. */
  dx: number
  dy: number
  /** `prefers-reduced-motion`: nur das Gleiten, kein Pop. */
  reduced?: boolean
  /** Dauer des Gleitens in ms. Vorgabe 350. */
  glide?: number
  /** Größe an der Spitze des Pops. Vorgabe 1,03. */
  peak?: number
  /** So weit hebt der Pop an, in px. Vorgabe 1. */
  lift?: number
  /** Dauer des Pops in ms. Vorgabe 700 (im Prototyp 400, nach dem Einbau verlängert, 03.10.2026). */
  pop?: number
  /** Anteil des Pops, der aufs Hochgehen fällt (0..1). Vorgabe 0,6. */
  rise?: number
  /** So weit federt sie beim Absinken unter die Normalgröße (0,025 = 2,5 %). */
  bounce?: number
  /** Wann im Gleiten der Pop beginnt (0..1). Vorgabe 0,2. */
  popStart?: number
  /** Schatten an der Spitze des Pops, als Anteil des Anhebe-Schattens. Vorgabe 0,5. */
  shadow?: number
}

export interface DropPopMotion {
  /** Weg und Größe — an das Element, das die Zeile verschiebt. */
  motion: Keyframe[]
  /**
   * Der Schatten — an das Element mit der sichtbaren Form (`.task-entry`, Pille mit Squircle).
   * An der Hülle, die verschoben wird, wäre er eckig: Sie hat keinen Radius (gemeldet 03.10.2026).
   */
  shadow: Keyframe[]
  /** Gesamtdauer in ms, für beide; zwischen den Stützstellen `easing: 'linear'`. */
  duration: number
}

/** Das Gleiten — dieselbe Kurve wie bisher der FLIP beim Ablegen. */
const glideEase = cubicBezier(0.25, 1, 0.5, 1)
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

/** Der Scheitel des Nachfederns liegt bei 60 % des Absinkens. */
const BOUNCE_SPLIT = 0.6
/** Abstand der Stützstellen in ms. */
const STEP_MS = 8

/** Pop-Verlauf 0..1 → h: 1 an der Spitze, 0 in Ruhe, unter 0 beim Nachfedern. */
function popShape(p: number, rise: number, under: number): number {
  if (p <= 0 || p >= 1) return 0
  if (p < rise) return easeOutCubic(p / rise)
  const q = (p - rise) / (1 - rise)
  if (under <= 0) return 1 - easeInOutCubic(q)
  if (q < BOUNCE_SPLIT) return 1 - (1 + under) * easeInOutCubic(q / BOUNCE_SPLIT)
  return -under * (1 - easeOutCubic((q - BOUNCE_SPLIT) / (1 - BOUNCE_SPLIT)))
}

/** Gerundet und ohne `-0` — die Werte landen als Text im Keyframe. */
const fmt = (value: number, digits: number) => {
  const rounded = Number(value.toFixed(digits))
  return Object.is(rounded, -0) ? 0 : rounded
}

/** Zwischen flach (0) und dem Anhebe-Schatten (1) — gleiche Liste, damit WAAPI überblenden kann. */
function shadowAt(k: number): string {
  const a = Math.max(0, k)
  return `0 ${fmt(1 + 11 * a, 1)}px ${fmt(2 + 26 * a, 1)}px rgba(0,0,0,${fmt(0.04 + 0.08 * a, 3)}), `
    + `0 ${fmt(4 * a, 1)}px ${fmt(10 * a, 1)}px rgba(0,0,0,${fmt(0.08 * a, 3)})`
}

export function dropPopMotion({
  dx, dy, reduced = false,
  glide = 350, peak = 1.03, lift = 1, pop = 700, rise = 0.6, bounce = 0.025, popStart = 0.2, shadow = 0.5,
}: DropPopMotionOptions): DropPopMotion {
  const popDuration = reduced ? 0 : pop
  const popBegin = glide * popStart
  const duration = Math.round(Math.max(glide, popBegin + popDuration))
  const under = peak > 1 ? Math.min(1, bounce / (peak - 1)) : 0
  const steps = Math.max(16, Math.round(duration / STEP_MS))

  const motion: Keyframe[] = []
  const shadowFrames: Keyframe[] = []
  for (let i = 0; i <= steps; i++) {
    const t = (duration * i) / steps
    const s = glide > 0 ? glideEase(Math.min(1, t / glide)) : 1
    const h = popDuration > 0 ? popShape((t - popBegin) / popDuration, rise, under) : 0
    const scale = (1.02 - 0.02 * s) * (1 + (peak - 1) * h)
    const y = dy * (1 - s) - lift * Math.max(0, h)
    const offset = i / steps
    motion.push({ offset, transform: `translate(${fmt(dx * (1 - s), 2)}px, ${fmt(y, 2)}px) scale(${fmt(scale, 4)})` })
    shadowFrames.push({ offset, boxShadow: shadowAt(1 - s + Math.max(0, h) * shadow) })
  }
  return { motion, shadow: shadowFrames, duration }
}
